import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Space, message, Tabs, Select, Typography, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from '../services/axiosConfig';
import { UTILITY_API } from '../constants/api';

const { TabPane } = Tabs;
const { Title } = Typography;

const UtilityManagement = () => {
    const [categories, setCategories] = useState([]);
    const [places, setPlaces] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isCatModalVisible, setIsCatModalVisible] = useState(false);
    const [isPlaceModalVisible, setIsPlaceModalVisible] = useState(false);
    const [editingCat, setEditingCat] = useState(null);
    const [editingPlace, setEditingPlace] = useState(null);

    const [catForm] = Form.useForm();
    const [placeForm] = Form.useForm();

    useEffect(() => {
        fetchUtilities();
    }, []);

    const fetchUtilities = async () => {
        setLoading(true);
        try {
            const response = await axios.get(UTILITY_API.LIST);
            setCategories(response.data.categories || []);
            
            // Flatten places object into an array
            let allPlaces = [];
            const placesData = response.data.places || {};
            for (const catId in placesData) {
                const catPlaces = placesData[catId].map(p => ({ ...p, CategoryID: catId }));
                allPlaces = [...allPlaces, ...catPlaces];
            }
            setPlaces(allPlaces);
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu tiện ích');
        } finally {
            setLoading(false);
        }
    };

    // Category Handlers
    const handleCatSubmit = async (values) => {
        try {
            if (editingCat) {
                await axios.put(UTILITY_API.CATEGORY_UPDATE(editingCat.id), values);
                message.success('Cập nhật danh mục thành công');
            } else {
                await axios.post(UTILITY_API.CATEGORY_CREATE, values);
                message.success('Thêm danh mục thành công');
            }
            setIsCatModalVisible(false);
            catForm.resetFields();
            fetchUtilities();
        } catch (error) {
            message.error('Lỗi khi lưu danh mục');
        }
    };

    const deleteCategory = async (id) => {
        try {
            await axios.delete(UTILITY_API.CATEGORY_DELETE(id));
            message.success('Xóa danh mục thành công');
            fetchUtilities();
        } catch (error) {
            message.error('Lỗi khi xóa danh mục');
        }
    };

    // Place Handlers
    const handlePlaceSubmit = async (values) => {
        const payload = {
            PlaceID: values.id,
            CategoryID: values.CategoryID,
            Name: values.name,
            ImageUrl: values.image_url,
            Rating: parseFloat(values.rating || 0),
            Address: values.address,
            OpenHours: values.open_hours,
            Distance: values.distance,
            Lat: parseFloat(values.Lat || 0),
            Lng: parseFloat(values.Lng || 0),
            Description: values.description,
            PhoneNumber: values.phone_number,
            Status: values.status || 'OPEN'
        };

        try {
            if (editingPlace) {
                await axios.put(UTILITY_API.PLACE_UPDATE(editingPlace.id), payload);
                message.success('Cập nhật địa điểm thành công');
            } else {
                await axios.post(UTILITY_API.PLACE_CREATE, payload);
                message.success('Thêm địa điểm thành công');
            }
            setIsPlaceModalVisible(false);
            placeForm.resetFields();
            fetchUtilities();
        } catch (error) {
            message.error('Lỗi khi lưu địa điểm');
        }
    };

    const deletePlace = async (id) => {
        try {
            await axios.delete(UTILITY_API.PLACE_DELETE(id));
            message.success('Xóa địa điểm thành công');
            fetchUtilities();
        } catch (error) {
            message.error('Lỗi khi xóa địa điểm');
        }
    };

    const catColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Tên danh mục', dataIndex: 'name', key: 'name' },
        { title: 'Icon URL', dataIndex: 'icon_url', key: 'icon_url', render: (url) => <img src={url} alt="icon" style={{width: 32, height: 32}} /> },
        {
            title: 'Hành động',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => {
                        setEditingCat(record);
                        catForm.setFieldsValue(record);
                        // Map id to CategoryID for form
                        catForm.setFieldsValue({ CategoryID: record.id });
                        setIsCatModalVisible(true);
                    }}>Sửa</Button>
                    <Popconfirm title="Bạn có chắc chắn muốn xóa danh mục này? Mọi địa điểm bên trong cũng sẽ bị xóa." onConfirm={() => deleteCategory(record.id)}>
                        <Button danger icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const placeColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Danh mục', dataIndex: 'CategoryID', key: 'CategoryID', render: (catId) => categories.find(c => c.id === catId)?.name || catId },
        { title: 'Tên địa điểm', dataIndex: 'name', key: 'name' },
        { title: 'Khoảng cách', dataIndex: 'distance', key: 'distance' },
        { title: 'Trạng thái', dataIndex: 'status', key: 'status' },
        {
            title: 'Hành động',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => {
                        setEditingPlace(record);
                        placeForm.setFieldsValue({
                            ...record,
                            Lat: record.coordinates?.lat,
                            Lng: record.coordinates?.lng
                        });
                        setIsPlaceModalVisible(true);
                    }}>Sửa</Button>
                    <Popconfirm title="Bạn có chắc chắn muốn xóa địa điểm này?" onConfirm={() => deletePlace(record.id)}>
                        <Button danger icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Card title={<Title level={4}>Quản lý Tiện ích xung quanh</Title>}>
            <Tabs defaultActiveKey="1">
                <TabPane tab="Danh mục tiện ích" key="1">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                        setEditingCat(null);
                        catForm.resetFields();
                        setIsCatModalVisible(true);
                    }} style={{ marginBottom: 16 }}>
                        Thêm danh mục
                    </Button>
                    <Table columns={catColumns} dataSource={categories} rowKey="id" loading={loading} />
                </TabPane>
                <TabPane tab="Địa điểm tiện ích" key="2">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                        setEditingPlace(null);
                        placeForm.resetFields();
                        setIsPlaceModalVisible(true);
                    }} style={{ marginBottom: 16 }}>
                        Thêm địa điểm
                    </Button>
                    <Table columns={placeColumns} dataSource={places} rowKey="id" loading={loading} scroll={{ x: 800 }} />
                </TabPane>
            </Tabs>

            {/* Modal for Category */}
            <Modal
                title={editingCat ? "Sửa danh mục" : "Thêm danh mục"}
                visible={isCatModalVisible}
                onCancel={() => setIsCatModalVisible(false)}
                onOk={() => catForm.submit()}
            >
                <Form form={catForm} layout="vertical" onFinish={handleCatSubmit}>
                    <Form.Item name="CategoryID" label="Mã danh mục (CategoryID)" rules={[{ required: true }]} tooltip="Ví dụ: cat_01">
                        <Input disabled={!!editingCat} />
                    </Form.Item>
                    <Form.Item name="Name" label="Tên danh mục" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="IconUrl" label="Icon URL">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal for Place */}
            <Modal
                title={editingPlace ? "Sửa địa điểm" : "Thêm địa điểm"}
                visible={isPlaceModalVisible}
                onCancel={() => setIsPlaceModalVisible(false)}
                onOk={() => placeForm.submit()}
                width={800}
            >
                <Form form={placeForm} layout="vertical" onFinish={handlePlaceSubmit}>
                    <Space size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Form.Item name="id" label="Mã địa điểm (PlaceID)" rules={[{ required: true }]} tooltip="Ví dụ: place_001">
                            <Input disabled={!!editingPlace} style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="CategoryID" label="Thuộc danh mục" rules={[{ required: true }]}>
                            <Select style={{ width: 350 }}>
                                {categories.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item name="name" label="Tên địa điểm" rules={[{ required: true }]}>
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="image_url" label="Image URL">
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="address" label="Địa chỉ">
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="open_hours" label="Giờ mở cửa (VD: 07:00 - 22:00)">
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="phone_number" label="Số điện thoại">
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="distance" label="Khoảng cách (VD: 300m)">
                            <Input style={{ width: 350 }} />
                        </Form.Item>
                        <Form.Item name="rating" label="Đánh giá (1-5)">
                            <Input type="number" step="0.1" style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="Lat" label="Vĩ độ (Latitude)">
                            <Input type="number" step="0.000001" style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="Lng" label="Kinh độ (Longitude)">
                            <Input type="number" step="0.000001" style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="status" label="Trạng thái">
                            <Select style={{ width: 150 }}>
                                <Select.Option value="OPEN">Mở cửa</Select.Option>
                                <Select.Option value="CLOSED">Đóng cửa</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space>
                    <Form.Item name="description" label="Mô tả chi tiết">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default UtilityManagement;
